import { useEffect, useRef, useState } from "react";
import { $authHost } from "../../http";
import { useAuth } from 'react-oidc-context';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    CircularProgress,
    Typography,
    AppBar,
    Toolbar,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export default function OnlyOfficeEditor(props) {
    const auth = useAuth();
    const docEditorRef = useRef(null);
    const { onClose, open = true } = props;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        console.log('OnlyOfficeEditor mounted with doc:', props.doc);

        // Проверяем обязательные параметры
        if (!props.doc || !props.doc.id || !props.doc.name || !props.doc.ext || !props.doc.file) {
            console.error('Missing required document parameters:', props.doc);
            setError('Отсутствуют обязательные параметры документа');
            setLoading(false);
            return;
        }

        if (!window.DocsAPI) {
            console.error('DocsAPI not loaded - check if script is included in index.html');
            setError('DocsAPI не загружен. Проверьте подключение скрипта.');
            setLoading(false);
            return;
        }

        console.log('DocsAPI is available:', !!window.DocsAPI);

        // Определяем тип документа на основе расширения
        const getDocumentType = (ext) => {
            const wordExts = ['doc', 'docx', 'rtf', 'odt', 'txt'];
            const excelExts = ['xls', 'xlsx', 'ods', 'csv'];
            const powerpointExts = ['ppt', 'pptx', 'odp'];
            
            if (wordExts.includes(ext)) return 'word';
            if (excelExts.includes(ext)) return 'cell';
            if (powerpointExts.includes(ext)) return 'slide';
            if (ext === 'pdf') return 'word'; // PDF обрабатывается как word
            return 'word'; // По умолчанию
        };

        const documentType = getDocumentType(props.doc.ext);

        const config = {
            document: {
                fileType: props.doc.ext,
                key: props.doc.id,
                title: props.doc.name,
                url: props.doc.file,
                permissions: {
                    edit: false,
                    print: true,
                    download: true
                },
            },
            editorConfig: {
                mode: 'view',
                customization: {
                    close: {
                        visible: true,
                        text: "Close file",
                    },
                },
                user: {
                    id: auth.user.profile.preferred_username,
                    name: auth.user.profile.given_name
                },
                events: {
                    onDocumentReady: () => {
                        console.log('Document ready');
                        setLoading(false);
                    },
                    onError: (error) => {
                        console.error('OnlyOffice error:', error);
                        setError('Ошибка при загрузке документа');
                        setLoading(false);
                    },
                    onRequestClose: () => {
                        console.log('onRequestClose');
                    }

                }
            },
            documentType: documentType
        };

        console.log('Getting token for document:', config);

        $authHost.post('/apig/get-token', config, {
            headers: {
                Authorization: `Bearer ${auth.user.access_token}`
            }
        }).then(res => {
            console.log('Token received, creating editor');
            try {
                const placeholder = document.getElementById("placeholder");
                if (!placeholder) {
                    console.error('Placeholder element not found');
                    return;
                }

                console.log('Creating DocEditor with config:', {
                    width: "100%",
                    height: "100%",
                    type: "desktop",
                    documentType: config.documentType,
                    document: config.document,
                    editorConfig: config.editorConfig,
                    token: res.data.token,
                });

                const docEditor = new window.DocsAPI.DocEditor("placeholder", {
                    width: "100%",
                    height: "100%",
                    type: "desktop",
                    documentType: config.documentType,
                    document: config.document,
                    editorConfig: config.editorConfig,
                    token: res.data.token,
                });

                console.log('DocEditor created successfully:', docEditor);
                docEditorRef.current = docEditor;
            } catch (error) {
                console.error('Error creating DocEditor:', error);
                setError('Ошибка при создании редактора');
                setLoading(false);
            }
        }).catch(error => {
            console.error('Error getting token:', error);
            setError('Ошибка при получении токена для документа');
            setLoading(false);
        });

        // Cleanup function
        return () => {
            if (docEditorRef.current) {
                try {
                    docEditorRef.current.destroyEditor();
                    docEditorRef.current = null;
                } catch (error) {
                    console.error('Error destroying editor:', error);
                }
            }
        };
    }, [props.doc, auth.user.access_token, auth.user.profile.preferred_username, auth.user.profile.given_name]);

    const handleClose = () => {
        if (docEditorRef.current) {
            try {
                docEditorRef.current.destroyEditor();
                docEditorRef.current = null;
            } catch (error) {
                console.error('Error destroying editor on close:', error);
            }
        }
        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={true}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    width: '100vw',
                    height: '100vh',
                    maxWidth: 'none',
                    maxHeight: 'none',
                    margin: 0,
                    // borderRadius: fullScreen ? 0 : 2,
                }
            }}
            sx={{
                zIndex: 10000,
                '& .MuiDialog-paper': {
                    margin: 0,
                    maxHeight: '100vh',
                    maxWidth: '100vw',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #2c3e50 0%, #4a6741 100%)',
                backgroundColor: '#2c3e50',
                color: 'white',
                padding: '2px 24px'
            }}>
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        flexGrow: 1,
                        fontSize: '1rem',
                        fontWeight: 500
                    }}
                >
                    {props.doc?.name || 'Документ'}
                </Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClose}
                    sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s ease-in-out'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    padding: 0,
                    height: 'calc(100vh - 64px)',
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >


                {/* Контейнер для редактора */}
                <div
                    id="placeholder"
                    style={{
                        height: "100%",
                        width: "100%",
                        position: 'relative',
                        opacity: loading || error ? 0 : 1,
                        transition: 'opacity 0.3s ease'
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}
